import { getWidgetById, type Widget } from "@/utils/widgets";
import { selectTemplate, onboardingTemplates, FALLBACK_TEMPLATE_ID } from "./index";
import type { TemplateWidgetSpec } from "./types";
import type { AssetClassId, OnboardingAnswers } from "../types";

/**
 * Suites worth a dashboard of their own, in the order we'd offer them.
 *
 * Keyed by the `accessGroup` that gates them, because that is exactly the
 * question being asked: what has this account paid for that the starter
 * dashboard never shows them? Sixteen subscriber-reachable widgets appear in
 * no starter template, and nine of those are the equities suite — an equities
 * trader entitled to SEC filings, insider transactions and government trades
 * currently finishes onboarding having seen none of it.
 */
const SUITES: {
  accessGroup: string;
  name: string;
  /**
   * Markets this suite is actually about. Entitlement alone is not a reason
   * to build someone a dashboard: an internal account, or anyone on a plan
   * that bundles the suite, holds `equities-suite-widgets` whether or not
   * they trade equities. Offering it regardless ignores everything they just
   * told us, which is the opposite of what onboarding is for.
   */
  assetClasses: AssetClassId[];
  widgets: TemplateWidgetSpec[];
}[] = [
  {
    accessGroup: "equities-suite-widgets",
    name: "Equities Suite",
    assetClasses: ["equities"],
    widgets: [
      { id: "SecFilings", size: { w: 6, h: 5 } },
      { id: "SecFilingStories", size: { w: 6, h: 5 } },
      { id: "OptionActivityFeed", size: { w: 4, h: 5 } },
      { id: "InsiderTransactionFeed", size: { w: 4, h: 5 } },
      { id: "GovernmentTradeFeed", size: { w: 4, h: 5 } },
      { id: "BlockTradeFeed", size: { w: 6, h: 4 } },
      { id: "Sec13FStocksFeed", size: { w: 6, h: 4 } },
    ],
  },
];

/**
 * Enough of a dashboard to be worth a tab.
 *
 * Two or three surviving widgets is a stub, not a showcase, and a second tab
 * that thin reads as a mistake — better to give one good dashboard than two
 * where the second is mostly empty.
 */
const MIN_WIDGETS = 4;

/**
 * What this user is actually interested in, from every signal we have.
 *
 * The markets they ticked are the direct answer, but they are optional now —
 * someone who only typed a sentence or pasted their book has none. So the
 * template their answers selected counts too (it encodes the same judgement),
 * and tickers imply equities, since a list of symbols is not a macro request.
 */
function effectiveAssetClasses(
  answers: OnboardingAnswers,
  primaryTemplateId: string
): Set<AssetClassId> {
  const out = new Set<AssetClassId>(answers.assetClasses);
  if (answers.tickers?.length) out.add("equities");
  const primary = onboardingTemplates.find((t) => t.id === primaryTemplateId);
  for (const ac of primary?.assetClasses ?? []) out.add(ac);
  return out;
}

export type SecondTab = {
  name: string;
  widgets: TemplateWidgetSpec[];
  /** For analytics / completion reporting. */
  id: string;
};

function accessible(
  specs: TemplateWidgetSpec[],
  hasAccessToWidget: (widget: Widget) => boolean
): TemplateWidgetSpec[] {
  return specs.filter((spec) => {
    const base = spec.id.includes("-") ? spec.id.split("-")[0] : spec.id;
    const widget = getWidgetById(base);
    return !!widget && hasAccessToWidget(widget);
  });
}

/**
 * The second dashboard, or null for none.
 *
 * Order of preference:
 *
 *  1. A suite the account is entitled to. Derived from entitlements in code
 *     rather than from the plan, which is why the concierge route gets this
 *     too without any change to the plan schema.
 *  2. Their second market, as an ordinary starter template — breadth when
 *     there is no depth to show.
 *  3. Nothing. One tab is a fine outcome and silence is the right way to say
 *     so; a half-empty second tab is worse than none.
 *
 * `primaryTemplateId` is excluded so nobody gets the same dashboard twice.
 */
export function resolveSecondTab(
  answers: OnboardingAnswers,
  primaryTemplateId: string,
  hasAccessToWidget: (widget: Widget) => boolean
): SecondTab | null {
  const relevant = effectiveAssetClasses(answers, primaryTemplateId);

  for (const suite of SUITES) {
    if (!suite.assetClasses.some((ac) => relevant.has(ac))) continue;
    const widgets = accessible(suite.widgets, hasAccessToWidget);
    if (widgets.length >= MIN_WIDGETS) {
      return { id: `suite-${suite.accessGroup}`, name: suite.name, widgets };
    }
  }

  // Their second market — the first asset class that doesn't lead back to the
  // dashboard they already have.
  for (const assetClass of answers.assetClasses) {
    const template = selectTemplate({
      assetClasses: [assetClass] as AssetClassId[],
    });
    if (template.id === primaryTemplateId) continue;
    // The multi-asset fallback is what an unmatched answer resolves to, so it
    // says nothing about this market in particular.
    if (template.id === FALLBACK_TEMPLATE_ID) continue;
    const widgets = accessible(template.widgets, hasAccessToWidget);
    if (widgets.length >= MIN_WIDGETS) {
      return { id: template.id, name: template.name, widgets };
    }
  }

  return null;
}
